import * as THREE from 'three';

interface CoffeeMachineConfig {
  bodyColor?: string;      // Main body color
  accentColor?: string;    // Color for buttons and details
  metalColor?: string;     // Color for metallic parts
  screenColor?: string;    // Color for the digital display
}

const DEFAULT_CONFIG: CoffeeMachineConfig = {
  bodyColor: '#1a1a1a',    // Dark gray for the main body
  accentColor: '#cc0000',  // Red for buttons and indicators
  metalColor: '#c0c0c0',   // Silver for metallic parts
  screenColor: '#00ff00'   // Green for the digital display
};

export function createCoffeeMachinePrimitive(config: Partial<CoffeeMachineConfig> = {}): THREE.Group {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const machineGroup = new THREE.Group();
  machineGroup.name = 'coffeeMachine';

  // Main body
  const bodyGeometry = new THREE.BoxGeometry(1.2, 0.8, 0.6);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.bodyColor,
    roughness: 0.2,
    metalness: 0.8
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  machineGroup.add(body);

  // Top section (bean hopper)
  const hopperGeometry = new THREE.CylinderGeometry(0.2, 0.15, 0.3, 8);
  const hopperMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.bodyColor,
    transparent: true,
    opacity: 0.6,
    roughness: 0.1
  });
  const hopper = new THREE.Mesh(hopperGeometry, hopperMaterial);
  hopper.position.set(-0.3, 0.55, 0);
  machineGroup.add(hopper);

  // Grinder section
  const grinderGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.2, 16);
  const grinderMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.metalColor,
    roughness: 0.3,
    metalness: 0.9
  });
  const grinder = new THREE.Mesh(grinderGeometry, grinderMaterial);
  grinder.position.set(-0.3, 0.3, 0);
  machineGroup.add(grinder);

  // Digital display
  const displayGeometry = new THREE.PlaneGeometry(0.3, 0.15);
  const displayMaterial = new THREE.MeshStandardMaterial({
    color: '#000000',
    emissive: finalConfig.screenColor,
    emissiveIntensity: 0.5
  });
  const display = new THREE.Mesh(displayGeometry, displayMaterial);
  display.position.set(0.2, 0.3, 0.301);
  machineGroup.add(display);

  // Control buttons
  const buttonGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.02, 16);
  const buttonMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.accentColor,
    roughness: 0.3,
    metalness: 0.7
  });

  // Add multiple buttons in a row
  const buttonPositions = [
    { x: 0.1, y: 0.15, z: 0.31 },
    { x: 0.2, y: 0.15, z: 0.31 },
    { x: 0.3, y: 0.15, z: 0.31 }
  ];

  buttonPositions.forEach(pos => {
    const button = new THREE.Mesh(buttonGeometry, buttonMaterial);
    button.position.set(pos.x, pos.y, pos.z);
    button.rotation.x = Math.PI / 2;
    machineGroup.add(button);
  });

  // Coffee spouts
  const spoutGeometry = new THREE.CylinderGeometry(0.02, 0.01, 0.1, 8);
  const spoutMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.metalColor,
    roughness: 0.1,
    metalness: 0.9
  });

  // Create two spouts
  [-0.1, 0.1].forEach(x => {
    const spout = new THREE.Mesh(spoutGeometry, spoutMaterial);
    spout.position.set(x, 0.1, 0.25);
    machineGroup.add(spout);
  });

  // Steam wand
  const steamWandGroup = new THREE.Group();
  
  // Base joint
  const jointGeometry = new THREE.SphereGeometry(0.03, 8, 8);
  const joint = new THREE.Mesh(jointGeometry, spoutMaterial);
  joint.position.set(0.5, 0.2, 0);
  steamWandGroup.add(joint);

  // Wand arm
  const wandGeometry = new THREE.CylinderGeometry(0.015, 0.01, 0.3, 8);
  const wand = new THREE.Mesh(wandGeometry, spoutMaterial);
  wand.position.set(0, -0.15, 0);
  wand.rotation.z = Math.PI / 4;
  joint.add(wand);

  // Wand tip
  const tipGeometry = new THREE.ConeGeometry(0.015, 0.05, 8);
  const tip = new THREE.Mesh(tipGeometry, spoutMaterial);
  tip.position.set(0, -0.17, 0);
  wand.add(tip);

  machineGroup.add(steamWandGroup);

  // Drip tray
  const trayGeometry = new THREE.BoxGeometry(0.8, 0.05, 0.3);
  const trayMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.metalColor,
    roughness: 0.4,
    metalness: 0.6
  });
  const tray = new THREE.Mesh(trayGeometry, trayMaterial);
  tray.position.set(0, -0.375, 0.15);
  machineGroup.add(tray);

  // Drip grate
  const grateGeometry = new THREE.PlaneGeometry(0.75, 0.25);
  const grateMaterial = new THREE.MeshStandardMaterial({
    color: '#333333',
    roughness: 0.8,
    metalness: 0.2
  });
  const grate = new THREE.Mesh(grateGeometry, grateMaterial);
  grate.rotation.x = -Math.PI / 2;
  grate.position.set(0, -0.35, 0.15);
  machineGroup.add(grate);

  // Add some grate details (lines)
  const lineGeometry = new THREE.BoxGeometry(0.75, 0.01, 0.01);
  const lineMaterial = new THREE.MeshStandardMaterial({
    color: finalConfig.metalColor
  });

  for (let i = 0; i < 5; i++) {
    const line = new THREE.Mesh(lineGeometry, lineMaterial);
    line.position.set(0, -0.345, 0.05 + i * 0.05);
    machineGroup.add(line);
  }

  return machineGroup;
} 